import React, { useCallback, useMemo } from 'react';
import InlineSVG from 'svg-inline-react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import * as R from 'ramda';

import useI18n from 'hooks/I18n/useI18n';
import useAnalytics from 'hooks/Analytics/useAnalytics';
import { BASKET_PAGE_ORIGIN } from 'modules/analytics';

import { userSelector } from 'modules/currentUser';
import { validateOrder, revalidateOrder, isOrderLoading } from 'modules/orders';
import { getLocalizedTime, getNextDayFullObject } from 'modules/utils/dateAndTime';
import config from 'modules/config';
import { capitalizeFirstLetter } from 'modules/utils/string';

import { isBasket } from 'models/orders';
import { saleHasEnded } from 'models/distributions';

import PIText from 'components/ProductIdentity/Text.jsx';
import Button, { SECONDARY_MODE, SMALL_SIZE } from 'src/components/atoms/Button/Button.jsx';
import Utils from 'modules/utils';

import ErrorIcon from 'app/assets/new-design/images/icons/error.svg';
import ValidateIcon from 'app/assets/new-design/images/icons/validate.svg';
import LoadingGrayIcon from 'app/assets/new-design/images/icons/loading-gray.svg';
import LoadingOrangeIcon from 'app/assets/new-design/images/icons/loading-orange.svg';
import PickupIcon from 'app/assets/new-design/images/icons/basket-pickup.svg';
import HomeDeliveryIcon from 'app/assets/new-design/images/icons/basket-home-delivery.svg';

const { apiLinkTo } = Utils;

const BasketOrderStatus = props => {
    const {
        order,
        distribution,
        getQueryParameters,
        hasDeliveryOptions,
        openCancelOrderModal,
        openResetToCartOrderModal,
        className,
        deliveryOptions,
    } = props;
    const { trans, i18n, transChoice } = useI18n();
    const dispatch = useDispatch();
    const history = useHistory();
    const user = useSelector(userSelector);
    const { analytics } = useAnalytics();
    const isLoading = useSelector(state => isOrderLoading(state, { orderId: order.id }));
    const validateOrderDispatch = useCallback(
        () => dispatch(validateOrder(order.id, hasDeliveryOptions, undefined, BASKET_PAGE_ORIGIN)),
        [order, hasDeliveryOptions, dispatch]
    );
    const revalidateOrderDispatch = useCallback(() => dispatch(revalidateOrder(order.id)), [
        dispatch,
        order.id,
    ]);
    const deliveryOptionSelected = order.deliveryOption.ref
        ? R.find(R.propEq('ref', order.deliveryOption.ref))(deliveryOptions)
        : R.find(R.propEq('type', 'assembly'))(deliveryOptions);

    const [statusLabel, buttonLabel, action, Icon, buttonMode] = useMemo(() => {
        if (isBasket(order)) {
            return [
                trans('order.status.inProgress'),
                trans(
                    deliveryOptions.length > 1
                        ? 'basket.button.deliveryPage'
                        : 'basket.button.deliveryPage.single'
                ),
                order.countItems > 0 && user.anonymous
                    ? () => {
                          analytics.trackGoToDeliveryPage(order.uuid, user, BASKET_PAGE_ORIGIN);
                          analytics.trackBeginCheckout(order, user);
                          return history.push(`/checkout/${order.uuid}/pickup`);
                      }
                    : validateOrderDispatch,
                <InlineSVG src={LoadingGrayIcon} />,
            ];
        }
        // Pending order
        if (order.state === 3) {
            return [
                trans('order.status.pending'),
                !saleHasEnded(distribution) &&
                    order.canBeResetToCart &&
                    trans('order.reset_to_cart.title'),
                !saleHasEnded(distribution) && order.canBeResetToCart && openResetToCartOrderModal,
                <InlineSVG src={LoadingOrangeIcon} color="red" />,
                SECONDARY_MODE,
            ];
        }
        // Validated order
        if (order.state === 4 || order.state === 5) {
            return [
                trans('order.status.validated'),
                !saleHasEnded(distribution)
                    ? trans('order.cancellation.title')
                    : trans('distributions.downloadOrder'),
                !saleHasEnded(distribution)
                    ? order.canBeCancelled && openCancelOrderModal
                    : () =>
                          window.open(
                              apiLinkTo(`orders/${order.id}.pdf{?q*}`, {
                                  hash: { q: getQueryParameters() },
                              }),
                              '_blank'
                          ),
                <InlineSVG src={ValidateIcon} color="red" />,
                SECONDARY_MODE,
            ];
        }
        if (order.isCancelledByTheMember || order.state === 9) {
            return [
                trans('distributions.finalize.reasons.canceled'),
                trans('basket.backToSale'),
                () =>
                    history.push(
                        `/assemblies/${distribution.hiveId}/collections/${distribution.id}/products`
                    ),
                <InlineSVG src={ErrorIcon} color="red" />,
            ];
        }
        // Cancelled, but not by member
        if (order.state === 7 || order.state === 2) {
            return [
                trans('order.status.canceled'),
                trans('basket.validateAgain'),
                revalidateOrderDispatch,
                <InlineSVG src={ErrorIcon} color="red" />,
            ];
        }
        return null;
    }, [
        order,
        trans,
        validateOrderDispatch,
        revalidateOrderDispatch,
        distribution,
        getQueryParameters,
        history,
        openCancelOrderModal,
        openResetToCartOrderModal,
        user,
        analytics,
        deliveryOptions,
    ]);

    const countDeliveryOfferType =
        deliveryOptions.filter(delivery => ['deliveryOffer'].includes(delivery.type)).length || 0;
    const countAssemblyPickupType =
        deliveryOptions.filter(delivery => ['assembly', 'pickup'].includes(delivery.type)).length ||
        0;
    const showAssemblyDelivery =
        !!deliveryOptions.find(delivery => ['assembly', 'pickup'].includes(delivery.type)) &&
        (['assembly', 'pickup'].includes(order.deliveryOption.type) || isBasket(order));

    const showHomeDelivery =
        countDeliveryOfferType > 0 &&
        (['deliveryOffer'].includes(order.deliveryOption.type) || isBasket(order));

    const hasOnlyOneAssemblyPickupOption = countAssemblyPickupType === 1 && !countDeliveryOfferType;
    const hasOnlyOneDeliveryOption = countDeliveryOfferType === 1 && !countAssemblyPickupType;
    const showOrderPickupSelected =
        (order.status !== 'basket' || hasOnlyOneAssemblyPickupOption || hasOnlyOneDeliveryOption) &&
        distribution.hiveId !== config.CROWDFARMING_HIVE_ID;

    return (
        <div className={classNames('basket-order-status-container', className)}>
            <div className="basket-order-status-content">
                <PIText
                    className="basket-order-status-title"
                    family="mr"
                    size="18px"
                    lineHeight="26px"
                    bold
                    color="gray2"
                >
                    {Icon}
                    {statusLabel}
                </PIText>
                <div className="delivery-option">
                    {showAssemblyDelivery && (
                        <PIText
                            family="mr"
                            size="14px"
                            lineHeight="20px"
                            weight={600}
                            color="gray2"
                        >
                            <InlineSVG src={PickupIcon} />
                            {transChoice('pickups.pickupPoints', countDeliveryOfferType)}
                        </PIText>
                    )}
                    {showHomeDelivery && (
                        <PIText
                            family="mr"
                            size="14px"
                            lineHeight="20px"
                            weight={600}
                            color="gray2"
                        >
                            <InlineSVG src={HomeDeliveryIcon} />
                            {trans('global.homeDelivery')}
                        </PIText>
                    )}
                </div>
                {showOrderPickupSelected && (
                    <>
                        {deliveryOptionSelected?.timeSlots?.map(timeSlot => {
                            const dateObject = getNextDayFullObject(
                                timeSlot,
                                distribution.distributionDate,
                                i18n
                            );
                            return (
                                <PIText
                                    className="basket-order-status-date"
                                    size="18px"
                                    lineHeight="26px"
                                    color="gray2"
                                    weight={600}
                                >
                                    {trans('assembly.distributionTimeWithDay', {
                                        '%weekday%': `${capitalizeFirstLetter(
                                            dateObject.weekday
                                        )} ${dateObject.day} ${dateObject.month}`,
                                        '%startHour%': getLocalizedTime(i18n, timeSlot.timeStart),
                                        '%endHour%': getLocalizedTime(i18n, timeSlot.timeEnd),
                                    })}
                                </PIText>
                            );
                        })}
                        {hasOnlyOneAssemblyPickupOption && (
                            <div className="basket-order-status-hive-address">
                                <PIText size="16px" lineHeight="24px" color="gray2" weight={600}>
                                    {distribution.place.name}
                                </PIText>
                                <PIText size="16px" lineHeight="24px" color="gray2">
                                    {distribution.place.address.street}
                                </PIText>
                                <PIText
                                    size="16px"
                                    lineHeight="24px"
                                    color="gray2"
                                >{`${distribution.place.address.city.zipCode} ${distribution.place.address.city.name}`}</PIText>
                            </div>
                        )}
                        {hasOnlyOneDeliveryOption && (
                            <PIText size="16px" lineHeight="24px" color="gray2" weight={600}>
                                {deliveryOptions.find(({ type }) => type === 'deliveryOffer').name}
                            </PIText>
                        )}
                    </>
                )}
            </div>
            {!saleHasEnded(distribution) &&
                (order.state === 4 || order.state === 5) &&
                !order.canBeCancelled && (
                    <PIText color="warning" size="12px" lineHeight="12px">
                        {trans('order.uncancelable.description')}
                    </PIText>
                )}
            {buttonLabel && (
                <Button
                    data-testId="delivery-button"
                    size={SMALL_SIZE}
                    disabled={!action}
                    onClick={action}
                    mode={buttonMode}
                >
                    {isLoading ? (
                        <div>
                            <img
                                width="auto"
                                height="16px"
                                src="/assets/images/loaderanims/loader-pi-white.gif"
                            />
                        </div>
                    ) : (
                        buttonLabel
                    )}
                </Button>
            )}
        </div>
    );
};

BasketOrderStatus.propTypes = {
    getQueryParameters: PropTypes.func.isRequired,
    order: PropTypes.object.isRequired,
    distribution: PropTypes.object.isRequired,
    hasDeliveryOptions: PropTypes.bool.isRequired,
    openCancelOrderModal: PropTypes.func,
    openResetToCartOrderModal: PropTypes.func,
    className: PropTypes.string,
    deliveryOptions: PropTypes.object,
};

export default BasketOrderStatus;
